/**
 * Error handling utilities for the Staking Decoding SDK.
 *
 * @remarks
 * Provides decoding- and validation-focused error types following BD-SDK patterns.
 */
/**
 * Base error class for Solana Staking SDK.
 */
export class DecodingError extends Error {
    protocol;
    details;
    originalError;
    constructor(message, options = {}) {
        super(message);
        this.name = "DecodingError";
        this.protocol = options.protocol;
        this.details = options.details;
        this.originalError = options.originalError;
        const anyError = Error;
        if (typeof anyError.captureStackTrace === "function") {
            // @ts-expect-error
            anyError.captureStackTrace(this, DecodingError);
        }
    }
}
/** Error when a transaction cannot be decoded or parsed */
export class TransactionDecodingError extends DecodingError {
    constructor(message, options = {}) {
        super(message, options);
        this.name = "TransactionDecodingError";
    }
}
/** Error thrown when validation rules are not satisfied */
export class ValidationError extends DecodingError {
    errors;
    constructor(message, errors = [], options = {}) {
        super(message, options);
        this.name = "ValidationError";
        this.errors = errors.slice();
    }
}
/** Error thrown when a protocol is not supported */
export class UnsupportedProtocolError extends DecodingError {
    constructor(protocol, options = {}) {
        super(`Unsupported protocol: ${protocol}`, {
            ...options,
            details: { protocol, ...options.details },
        });
        this.name = "UnsupportedProtocolError";
    }
}
/**
 * Type guard to check if an error is a Decoding SDK error.
 *
 * @param error - Error to check
 * @returns True if the error is a Decoding SDK error
 *
 * @example
 * ```typescript
 * try {
 *   await sdk.solanaStaking.createStakeAccount();
 * } catch (error) {
 *   if (isSolanaStakingError(error)) {
 *     console.log("SDK Error:", error.message, error.code);
 *   } else {
 *     console.log("Unexpected error:", error);
 *   }
 * }
 * ```
 */
export function isDecodingError(error) {
    if (typeof error !== "object" || error === null)
        return false;
    const maybe = error;
    const name = typeof maybe.name === "string" ? maybe.name : undefined;
    const hasMessage = typeof maybe.message === "string";
    const isKnownName = name === "DecodingError" ||
        name === "TransactionDecodingError" ||
        name === "ValidationError" ||
        name === "UnsupportedProtocolError";
    return hasMessage && (error instanceof DecodingError || isKnownName);
}
export class SolanaStakingError extends DecodingError {
}
export class SolanaTransactionError extends TransactionDecodingError {
}
export class SolanaValidatorError extends ValidationError {
}
export class SolanaRpcError extends DecodingError {
}
export const isSolanaStakingError = isDecodingError;
export function logDecodingError(logger, err) {
    if (isDecodingError(err)) {
        const base = {
            name: err.name,
            message: err.message,
            protocol: err.protocol,
            details: err.details,
        };
        if (err instanceof ValidationError) {
            logger.error("ValidationError", { ...base, errors: err.errors });
        }
        else if (err instanceof TransactionDecodingError) {
            logger.error("TransactionDecodingError", base);
        }
        else {
            logger.error("DecodingError", base);
        }
    }
    else if (err instanceof Error) {
        logger.error("Error", { name: err.name, message: err.message });
    }
    else {
        logger.error("Unknown error", { value: err });
    }
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiZXJyb3IuanMiLCJzb3VyY2VSb290IjoiIiwic291cmNlcyI6WyIuLi8uLi8uLi8uLi9zcmMvbWFpbi9zaGFyZWQvZXJyb3IudHMiXSwibmFtZXMiOltdLCJtYXBwaW5ncyI6IkFBQUE7Ozs7O0dBS0c7QUFnQkg7O0dBRUc7QUFDSCxNQUFNLE9BQU8sYUFBYyxTQUFRLEtBQUs7SUFDdEIsUUFBUSxDQUFVO0lBQ2xCLE9BQU8sQ0FBVztJQUNsQixhQUFhLENBQVM7SUFFdEMsWUFDRSxPQUFlLEVBQ2YsVUFJSSxFQUFFO1FBRU4sS0FBSyxDQUFDLE9BQU8sQ0FBQyxDQUFDO1FBQ2YsSUFBSSxDQUFDLElBQUksR0FBRyxlQUFlLENBQUM7UUFDNUIsSUFBSSxDQUFDLFFBQVEsR0FBRyxPQUFPLENBQUMsUUFBUSxDQUFDO1FBQ2pDLElBQUksQ0FBQyxPQUFPLEdBQUcsT0FBTyxDQUFDLE9BQU8sQ0FBQztRQUMvQixJQUFJLENBQUMsYUFBYSxHQUFHLE9BQU8sQ0FBQyxhQUFhLENBQUM7UUFFM0MsTUFBTSxRQUFRLEdBQUcsS0FFaEIsQ0FBQztRQUNGLElBQUksT0FBTyxRQUFRLENBQUMsaUJBQWlCLEtBQUssVUFBVSxFQUFFLENBQUM7WUFDckQsbUJBQW1CO1lBQ25CLFFBQVEsQ0FBQyxpQkFBaUIsQ0FBQyxJQUFJLEVBQUUsYUFBYSxDQUFDLENBQUM7UUFDbEQsQ0FBQztJQUNILENBQUM7Q0FDRjtBQUVELDJEQUEyRDtBQUMzRCxNQUFNLE9BQU8sd0JBQXlCLFNBQVEsYUFBYTtJQUN6RCxZQUNFLE9BQWUsRUFDZixVQUlJLEVBQUU7UUFFTixLQUFLLENBQUMsT0FBTyxFQUFFLE9BQU8sQ0FBQyxDQUFDO1FBQ3hCLElBQUksQ0FBQyxJQUFJLEdBQUcsMEJBQTBCLENBQUM7SUFDekMsQ0FBQztDQUNGO0FBRUQsMkRBQTJEO0FBQzNELE1BQU0sT0FBTyxlQUFnQixTQUFRLGFBQWE7SUFDaEMsTUFBTSxDQUFXO0lBQ2pDLFlBQ0UsT0FBZSxFQUNmLFNBQW1CLEVBQUUsRUFDckIsVUFJSSxFQUFFO1FBRU4sS0FBSyxDQUFDLE9BQU8sRUFBRSxPQUFPLENBQUMsQ0FBQztRQUN4QixJQUFJLENBQUMsSUFBSSxHQUFHLGlCQUFpQixDQUFDO1FBQzlCLElBQUksQ0FBQyxNQUFNLEdBQUcsTUFBTSxDQUFDLEtBQUssRUFBRSxDQUFDO0lBQy9CLENBQUM7Q0FDRjtBQUVELG9EQUFvRDtBQUNwRCxNQUFNLE9BQU8sd0JBQXlCLFNBQVEsYUFBYTtJQUN6RCxZQUNFLFFBQWdCLEVBQ2hCLFVBR0ksRUFBRTtRQUVOLEtBQUssQ0FBQyx5QkFBeUIsUUFBUSxFQUFFLEVBQUU7WUFDekMsR0FBRyxPQUFPO1lBQ1YsT0FBTyxFQUFFLEVBQUUsUUFBUSxFQUFFLEdBQUksT0FBTyxDQUFDLE9BQWtCLEVBQUU7U0FDdEQsQ0FBQyxDQUFDO1FBQ0gsSUFBSSxDQUFDLElBQUksR0FBRywwQkFBMEIsQ0FBQztJQUN6QyxDQUFDO0NBQ0Y7QUFFRDs7Ozs7Ozs7Ozs7Ozs7Ozs7O0dBa0JHO0FBQ0gsTUFBTSxVQUFVLGVBQWUsQ0FBQyxLQUFjO0lBQzVDLElBQUksT0FBTyxLQUFLLEtBQUssUUFBUSxJQUFJLEtBQUssS0FBSyxJQUFJO1FBQUUsT0FBTyxLQUFLLENBQUM7SUFFOUQsTUFBTSxLQUFLLEdBQWlELEtBRTNELENBQUM7SUFDRixNQUFNLElBQUksR0FBRyxPQUFPLEtBQUssQ0FBQyxJQUFJLEtBQUssUUFBUSxDQUFDLENBQUMsQ0FBQyxLQUFLLENBQUMsSUFBSSxDQUFDLENBQUMsQ0FBQyxTQUFTLENBQUM7SUFDckUsTUFBTSxVQUFVLEdBQUcsT0FBTyxLQUFLLENBQUMsT0FBTyxLQUFLLFFBQVEsQ0FBQztJQUVyRCxNQUFNLFdBQVcsR0FDZixJQUFJLEtBQUssZUFBZTtRQUN4QixJQUFJLEtBQUssMEJBQTBCO1FBQ25DLElBQUksS0FBSyxpQkFBaUI7UUFDMUIsSUFBSSxLQUFLLDBCQUEwQixDQUFDO0lBRXRDLE9BQU8sVUFBVSxJQUFJLENBQUMsS0FBSyxZQUFZLGFBQWEsSUFBSSxXQUFXLENBQUMsQ0FBQztBQUN2RSxDQUFDO0FBSUQsTUFBTSxPQUFPLGtCQUFtQixTQUFRLGFBQWE7Q0FBRztBQUN4RCxNQUFNLE9BQU8sc0JBQXVCLFNBQVEsd0JBQXdCO0NBQUc7QUFDdkUsTUFBTSxPQUFPLG9CQUFxQixTQUFRLGVBQWU7Q0FBRztBQUM1RCxNQUFNLE9BQU8sY0FBZSxTQUFRLGFBQWE7Q0FBRztBQUNwRCxNQUFNLENBQUMsTUFBTSxvQkFBb0IsR0FBRyxlQUFlLENBQUM7QUFJcEQsTUFBTSxVQUFVLGdCQUFnQixDQUFDLE1BQWUsRUFBRSxHQUFZO0lBQzVELElBQUksZUFBZSxDQUFDLEdBQUcsQ0FBQyxFQUFFLENBQUM7UUFDekIsTUFBTSxJQUFJLEdBQUc7WUFDWCxJQUFJLEVBQUcsR0FBYSxDQUFDLElBQUk7WUFDekIsT0FBTyxFQUFFLEdBQUcsQ0FBQyxPQUFPO1lBQ3BCLFFBQVEsRUFBRSxHQUFHLENBQUMsUUFBUTtZQUN0QixPQUFPLEVBQUUsR0FBRyxDQUFDLE9BQU87U0FDWixDQUFDO1FBQ1gsSUFBSSxHQUFHLFlBQVksZUFBZSxFQUFFLENBQUM7WUFDbkMsTUFBTSxDQUFDLEtBQUssQ0FBQyxpQkFBaUIsRUFBRSxFQUFFLEdBQUcsSUFBSSxFQUFFLE1BQU0sRUFBRSxHQUFHLENBQUMsTUFBTSxFQUFFLENBQUMsQ0FBQztRQUNuRSxDQUFDO2FBQU0sSUFBSSxHQUFHLFlBQVksd0JBQXdCLEVBQUUsQ0FBQztZQUNuRCxNQUFNLENBQUMsS0FBSyxDQUFDLDBCQUEwQixFQUFFLElBQUksQ0FBQyxDQUFDO1FBQ2pELENBQUM7YUFBTSxDQUFDO1lBQ04sTUFBTSxDQUFDLEtBQUssQ0FBQyxlQUFlLEVBQUUsSUFBSSxDQUFDLENBQUM7UUFDdEMsQ0FBQztJQUNILENBQUM7U0FBTSxJQUFJLEdBQUcsWUFBWSxLQUFLLEVBQUUsQ0FBQztRQUNoQyxNQUFNLENBQUMsS0FBSyxDQUFDLE9BQU8sRUFBRSxFQUFFLElBQUksRUFBRSxHQUFHLENBQUMsSUFBSSxFQUFFLE9BQU8sRUFBRSxHQUFHLENBQUMsT0FBTyxFQUFFLENBQUMsQ0FBQztJQUNsRSxDQUFDO1NBQU0sQ0FBQztRQUNOLE1BQU0sQ0FBQyxLQUFLLENBQUMsZUFBZSxFQUFFLEVBQUUsS0FBSyxFQUFFLEdBQUcsRUFBRSxDQUFDLENBQUM7SUFDaEQsQ0FBQztBQUNILENBQUMifQ==